import { create } from "zustand";

/**
 * Lightweight store for pages to publish their current state so Felix can
 * understand what the user is looking at. Pages call `setPageContext()` on
 * mount / state change; FelixChat reads it when sending messages.
 */

/**
 * The ONE record a page currently has open, when a page has such a thing.
 *
 * ROW b1b55364. Bob, 2026-09-12: "1a1da57f=agreed, do that." A single document
 * and a single meeting must report themselves apart from their lists, so Felix
 * can help with the one that is open rather than with the list it came from.
 *
 * WHY THIS IS NOT DERIVED FROM THE URL. Documents.tsx and Meetings.tsx open
 * their detail panel in COMPONENT STATE and never change the pathname, so
 * getPageName(location.pathname) returns "Documents" and "Meetings" whether the
 * panel is open or shut. There is nothing in the URL to read. The page is the
 * only thing that knows, so the page says so.
 *
 * WHY `pageName` TRAVELS WITH THE ID rather than being inferred backend-side
 * from entity_type: the backend already has triggers keyed on the literal names
 * "DocumentDetail" and "MeetingDetail" (persona.py), and a second mapping from
 * entity_type to page name would be a second place for the two to disagree.
 */
export interface OpenEntity {
  /** Canonical entity-type slug, e.g. "document", "meeting". */
  type: string;
  /** The record's id. */
  id: string;
  /** The page_name Felix should see while this record is open. */
  pageName: string;
  /**
   * Human title of the open record. OPTIONAL AND ADVISORY, and the comment
   * here used to overstate it: it said "for the page context Felix reads",
   * which it is not — nothing sends it anywhere. Felix learns the record's
   * title from the BACKEND, which loads it by entity_id (assistant's
   * fetch_open_record), so shipping the client's copy would be a second source
   * of the same fact that could disagree with the first.
   *
   * It is kept because it is the natural thing for a future in-page affordance
   * to read ("approve the decision in <title>?") and it costs one field. A
   * reader should not infer from its presence that the server sees it.
   */
  title?: string;
}

export interface PageContextState {
  /** Active filters on the current page (e.g. { status: "OPEN", severity: "HIGH" }) */
  activeFilters: Record<string, string>;
  /** Current search query typed in the page's search box */
  searchQuery: string;
  /** IDs of items the user has selected (e.g. checkboxes in a table) */
  selectedItems: string[];
  /** Name of a dialog/modal currently open (e.g. "create_objective") */
  openDialog: string | null;
  /** The ONE record this page has open, if it has one. See OpenEntity. */
  openEntity: OpenEntity | null;

  /** Pages call this to update context. Partial updates are merged. */
  setPageContext: (ctx: Partial<Pick<PageContextState, "activeFilters" | "searchQuery" | "selectedItems" | "openDialog" | "openEntity">>) => void;
  /** Reset when navigating away */
  clearPageContext: () => void;
}

const EMPTY: Pick<PageContextState, "activeFilters" | "searchQuery" | "selectedItems" | "openDialog" | "openEntity"> = {
  activeFilters: {},
  searchQuery: "",
  selectedItems: [],
  openDialog: null,
  openEntity: null,
};

export const usePageContextStore = create<PageContextState>((set) => ({
  ...EMPTY,
  setPageContext: (ctx) => set((state) => ({ ...state, ...ctx })),
  clearPageContext: () => set(EMPTY),
}));
